import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { arrayify } from '@ethersproject/bytes'
import { parseBytes32String } from '@ethersproject/strings'
import { Currency, ERC20Token, Token } from '@nominex/nomiswap-sdk'
import { createSelector } from '@reduxjs/toolkit'
import { useWagmi } from 'libs/wagmi'
import {
  combinedTokenMapFromActiveUrlsSelector,
  combinedTokenMapFromOfficialsUrlsSelector,
  TokenAddressMap,
  useUnsupportedTokenList,
} from 'state/lists/hooks'
import { NEVER_RELOAD, useSingleCallResult } from 'state/multicall/hooks'
import useUserAddedTokens, { userAddedTokenSelector } from 'state/user/hooks/useUserAddedTokens'
import { isAddress } from 'utils'
import { useBep20, useBytes32TokenContract } from './useContract'
import useNativeCurrency from './useNativeCurrency'

const mapWithoutUrls = (tokenMap: TokenAddressMap, chainId: number) =>
  Object.keys(tokenMap[chainId] || {}).reduce<{ [address: string]: Token }>((newMap, address) => {
    // eslint-disable-next-line
    newMap[address] = tokenMap[chainId][address].token
    return newMap
  }, {})

const allTokenSelector = (chainId: number) =>
  createSelector(
    [combinedTokenMapFromActiveUrlsSelector, userAddedTokenSelector(chainId)],
    (tokenMap, userAddedTokens) =>
      userAddedTokens
        // reduce into all ALL_TOKENS filtered by the current chain
        .reduce<{ [address: string]: Token }>(
          (acc, token) => {
            acc[token.address] = token
            return acc
          },
          // must make a copy because reduce modifies the map, and we do not
          // want to make a copy in every iteration
          mapWithoutUrls(tokenMap, chainId),
        ),
  )

const allOfficialsAndUserAddedTokensSelector = (chainId: number) =>
  createSelector(
    [combinedTokenMapFromOfficialsUrlsSelector, userAddedTokenSelector(chainId)],
    (tokenMap, userAddedTokens) =>
      userAddedTokens
        // reduce into all ALL_TOKENS filtered by the current chain
        .reduce<{ [address: string]: Token }>(
          (acc, token) => {
            acc[token.address] = token
            return acc
          },
          // must make a copy because reduce modifies the map, and we do not
          // want to make a copy in every iteration
          mapWithoutUrls(tokenMap, chainId),
        ),
  )

/**
 * Returns all tokens that are from active urls and user added tokens
 */
export function useAllTokens(): { [address: string]: Token } {
  const { chainId } = useWagmi()
  return useSelector(allTokenSelector(chainId))
}

/**
 * Returns all tokens that are from officials token list and user added tokens
 */
export function useOfficialsAndUserAddedTokens(): { [address: string]: Token } {
  const { chainId } = useWagmi()
  return useSelector(allOfficialsAndUserAddedTokensSelector(chainId))
}

export function useUnsupportedTokens(): { [address: string]: Token } {
  const { chainId } = useWagmi()
  const unsupportedTokensMap = useUnsupportedTokenList()
  return useMemo(() => mapWithoutUrls(unsupportedTokensMap, chainId), [unsupportedTokensMap, chainId])
}

export function useIsTokenActive(token: Token | undefined | null): boolean {
  const activeTokens = useAllTokens()

  if (!activeTokens || !token) {
    return false
  }

  return !!activeTokens[token.address]
}

// Check if currency is included in custom list from user storage
export function useIsUserAddedToken(currency: Currency | undefined | null): boolean {
  const userAddedTokens = useUserAddedTokens()

  if (!currency) {
    return false
  }

  return !!userAddedTokens.find((token) => currency?.equals(token))
}

// parse a name or symbol from a token response
const BYTES32_REGEX = /^0x[a-fA-F0-9]{64}$/

function parseStringOrBytes32(str: string | undefined, bytes32: string | undefined, defaultValue: string): string {
  return str && str.length > 0
    ? str
    : // need to check for proper bytes string and valid terminator
      bytes32 && BYTES32_REGEX.test(bytes32) && arrayify(bytes32)[31] === 0
      ? parseBytes32String(bytes32)
      : defaultValue
}

// undefined if invalid or does not exist
// null if loading
// otherwise returns the token
export function useToken(tokenAddress?: string): ERC20Token | undefined | null {
  const { chainId } = useWagmi()
  const tokens = useAllTokens()

  const address = isAddress(tokenAddress)

  const tokenContract = useBep20(address || undefined)
  const tokenContractBytes32 = useBytes32TokenContract(address || undefined)
  const token: Token | undefined = address ? tokens[address] : undefined

  const tokenName = useSingleCallResult(token ? undefined : tokenContract, 'name', undefined, NEVER_RELOAD)
  const tokenNameBytes32 = useSingleCallResult(
    token ? undefined : tokenContractBytes32,
    'name',
    undefined,
    NEVER_RELOAD,
  )
  const symbol = useSingleCallResult(token ? undefined : tokenContract, 'symbol', undefined, NEVER_RELOAD)
  const symbolBytes32 = useSingleCallResult(token ? undefined : tokenContractBytes32, 'symbol', undefined, NEVER_RELOAD)
  const decimals = useSingleCallResult(token ? undefined : tokenContract, 'decimals', undefined, NEVER_RELOAD)

  return useMemo(() => {
    if (token) return token
    if (!chainId || !address) return undefined
    if (decimals.loading || symbol.loading || tokenName.loading) return null
    if (decimals.result) {
      return new Token(
        chainId,
        address,
        decimals.result[0],
        parseStringOrBytes32(symbol.result?.[0], symbolBytes32.result?.[0], 'UNKNOWN'),
        parseStringOrBytes32(tokenName.result?.[0], tokenNameBytes32.result?.[0], 'Unknown Token'),
      )
    }
    return undefined
  }, [
    address,
    chainId,
    decimals.loading,
    decimals.result,
    symbol.loading,
    symbol.result,
    symbolBytes32.result,
    token,
    tokenName.loading,
    tokenName.result,
    tokenNameBytes32.result,
  ])
}

export function useCurrency(currencyId: string | undefined): Currency | ERC20Token | null | undefined {
  const native = useNativeCurrency()
  const isNative = currencyId?.toUpperCase() === native.symbol?.toUpperCase()
  const token = useToken(isNative ? undefined : currencyId)
  return isNative ? native : token
}
